import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { OwnerHandoffView } from "../../../../../database/mediaOps/mediaOpsApi";
import { browserTimeZone, formatInZone, localPartsInZone, resolveLocalTime } from "../../../../../database/mediaOps/zonedTime";
import { copyText, downloadSequentially, useMutation } from "./hooks";
import { publicationChip, publicationSource } from "./stateLabels";
import { Banner, FieldLabel, Hint, Mono, Panel, PanelHead, ResultNotice, Small, TextInput } from "./ui";

// The owner pack as an admin sees it: caption, ordered files, downloads, an
// explicit "Mark as posted" form, and access link reissue. Nothing here runs
// on render except the read. Publication is only reported by a button press.
// Stacked layout with large tap targets so it works at 375px.
//
// Download links are signed and short-lived. The panel shows when they
// expire, refreshes them (a new get_owner_handoff read) when they are within
// a minute of expiry, before any download, and after a failed download.

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);

  & > * {
    min-height: 44px;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    & > * {
      width: 100%;
    }
  }
`;

const Caption = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.5;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text);
`;

const FileRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: 10px 12px;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  min-height: 44px;

  a {
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    color: var(--color-info);
    padding: 8px 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

/** Refresh signed links this long before they expire. */
export const LINK_REFRESH_MARGIN_MS = 60_000;

/** The earliest expiry among the pack's signed links, in ms. Null when none is known. */
export const linksExpireAtMs = (pack: OwnerHandoffView | null): number | null => {
  if (!pack) return null;
  const times = [...(pack.manifest?.media || []).map((m) => m.expiresAt), pack.captionFile?.expiresAt]
    .map((iso) => (typeof iso === "string" ? Date.parse(iso) : NaN))
    .filter((ms) => Number.isFinite(ms));
  return times.length ? Math.min(...times) : null;
};

const sizeLabel = (n?: number) => (typeof n === "number" ? (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`) : "");

export const OwnerPackPanel: React.FC<{ communityId: string; handoffId: string; onChanged?: () => void }> = ({
  communityId,
  handoffId,
  onChanged,
}) => {
  const [pack, setPack] = useState<OwnerHandoffView | null>(null);
  const [loadErr, setLoadErr] = useState("");
  const [copied, setCopied] = useState("");
  const [downloading, setDownloading] = useState(false);
  const tz = browserTimeZone();
  const initial = localPartsInZone(Date.now(), tz);
  const [pubDate, setPubDate] = useState(initial.date);
  const [pubTime, setPubTime] = useState(initial.time);
  const [pubUrl, setPubUrl] = useState("");
  const [timeErr, setTimeErr] = useState("");
  const report = useMutation();
  const reissue = useMutation<{ link?: string | null; expiresAt?: string }>();
  const [link, setLink] = useState<string | null>(null);
  const [linkNote, setLinkNote] = useState("");
  const packRef = useRef<OwnerHandoffView | null>(null);

  /** Reads the pack (fresh signed links). Returns it, or null when blocked. */
  const load = useCallback(async (): Promise<OwnerHandoffView | null> => {
    const r = await mediaOps.getHandoff(communityId, handoffId);
    if (r.status === "blocked") {
      setLoadErr(r.errors.map((e) => `${e.code}: ${e.message}`).join(" "));
      return null;
    }
    setLoadErr("");
    packRef.current = r.data;
    setPack(r.data);
    return r.data;
  }, [communityId, handoffId]);
  useEffect(() => {
    load();
  }, [load]);

  // Refresh the links a minute before they expire while the panel is open.
  const expiresAtMs = linksExpireAtMs(pack);
  useEffect(() => {
    if (expiresAtMs === null) return undefined;
    const delay = expiresAtMs - LINK_REFRESH_MARGIN_MS - Date.now();
    if (delay <= 0) return undefined; // already close: the next download refreshes first
    const timer = setTimeout(() => {
      load();
    }, Math.min(delay, 2_147_000_000));
    return () => clearTimeout(timer);
  }, [expiresAtMs, load]);

  /** The pack with links that are good for at least another minute. */
  const freshPack = async (): Promise<OwnerHandoffView | null> => {
    const current = packRef.current;
    const exp = linksExpireAtMs(current);
    if (exp !== null && exp - Date.now() <= LINK_REFRESH_MARGIN_MS) return (await load()) || current;
    return current;
  };

  if (!pack) return <Small>{loadErr || "Loading owner pack."}</Small>;

  const caption = pack.manifest?.caption ?? "";
  const sortedMedia = (p: OwnerHandoffView | null) => (p?.manifest?.media || []).slice().sort((a, b) => a.order - b.order);
  const downloadableOf = (p: OwnerHandoffView | null) =>
    sortedMedia(p).filter((m) => !!m.downloadUrl) as { downloadUrl: string; fileName: string }[];
  const media = sortedMedia(pack);
  const downloadable = downloadableOf(pack);
  const pub = publicationChip(pack.publication?.state || "unconfirmed", publicationSource(pack.publication));

  /** Downloads with fresh links; a failure refreshes the links and says so. */
  const runDownload = async (pick: (p: OwnerHandoffView | null) => { url: string; fileName: string }[]) => {
    setLinkNote("");
    try {
      const files = pick(await freshPack());
      if (files.length === 0) throw new Error("no link");
      await downloadSequentially(files);
    } catch {
      await load();
      setLinkNote("A download did not start. The links were refreshed. Try again.");
    }
  };

  const onCopy = async () => {
    const ok = await copyText(caption);
    setCopied(ok ? "Caption copied." : "Copy failed. Select the caption and copy it by hand.");
  };
  const refreshLinks = async () => {
    const fresh = await load();
    setLinkNote(fresh ? "Links refreshed." : "Could not refresh the links. Try again.");
  };
  const onDownloadAll = async () => {
    setDownloading(true);
    try {
      await runDownload((p) => downloadableOf(p).map((m) => ({ url: m.downloadUrl, fileName: m.fileName })));
    } finally {
      setDownloading(false);
    }
  };
  const onDownloadOne = (e: React.MouseEvent, order: number, fileName: string) => {
    const exp = linksExpireAtMs(packRef.current);
    if (exp === null || exp - Date.now() > LINK_REFRESH_MARGIN_MS) return; // the link is fresh: let the anchor work
    e.preventDefault();
    runDownload((p) =>
      sortedMedia(p)
        .filter((m) => m.order === order && m.fileName === fileName && !!m.downloadUrl)
        .map((m) => ({ url: m.downloadUrl as string, fileName: m.fileName }))
    );
  };
  const onReport = async () => {
    let publishedAt: string;
    try {
      const r = resolveLocalTime(pubDate, pubTime, tz);
      if (r.kind === "nonexistent") throw new Error("That time does not exist here because of a clock change.");
      publishedAt = r.iso;
    } catch (e: any) {
      report.clear();
      setTimeErr(e?.message || "Enter a valid date and time.");
      return;
    }
    setTimeErr("");
    const res = await report.run((k) =>
      mediaOps.reportPublication(k, communityId, {
        handoffId,
        publishedAt,
        ...(pubUrl.trim() ? { publishedUrl: pubUrl.trim() } : {}),
      })
    );
    if (res && res.status !== "blocked") {
      await load();
      onChanged?.();
    }
  };
  const onReissue = async () => {
    const res = await reissue.run((k) => mediaOps.reissueHandoff(k, communityId, handoffId));
    setLink(res?.data?.link || null);
  };

  return (
    <Panel data-testid="owner-pack">
      <PanelHead>Owner pack for @{pack.handle}</PanelHead>
      <Stack>
        <Small>Intended publish {formatInZone(pack.intendedPublishAt, pack.timezone)}</Small>
        <div>
          <Tag size="sm" variant={pub.variant}>
            Publication: {pub.label}
          </Tag>{" "}
          {pack.emailStatus ? <Tag size="sm">Email: {pack.emailStatus}, not published</Tag> : null}
        </div>

        <div>
          <Small>Caption</Small>
          <Caption data-testid="pack-caption">{caption}</Caption>
        </div>
        <Actions>
          <Button variant="secondary" size="lg" onClick={onCopy}>
            Copy caption
          </Button>
          <Button variant="secondary" size="lg" onClick={onDownloadAll} disabled={downloadable.length === 0} isLoading={downloading}>
            Download all ({downloadable.length})
          </Button>
        </Actions>
        {copied ? <Small role="status">{copied}</Small> : null}

        <Stack>
          {media.map((m, i) => (
            <FileRow key={`${m.order}-${m.fileName}`}>
              <Small>
                {i + 1}. {m.fileName} ({m.type}
                {m.sizeBytes ? `, ${sizeLabel(m.sizeBytes)}` : ""})
              </Small>
              {m.downloadUrl ? (
                <a
                  href={m.downloadUrl}
                  download={m.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => onDownloadOne(e, m.order, m.fileName)}
                >
                  Download
                </a>
              ) : (
                <Small>No download link yet.</Small>
              )}
            </FileRow>
          ))}
        </Stack>
        {media.some((m) => m.downloadUrl) ? (
          <Hint data-testid="links-expiry">
            {expiresAtMs !== null
              ? `Download links work until ${formatInZone(new Date(expiresAtMs).toISOString(), tz)}. They refresh on their own before then.`
              : "Download links expire after a short time."}
          </Hint>
        ) : null}
        <Actions>
          <Button variant="ghost" size="lg" onClick={refreshLinks}>
            Get fresh links
          </Button>
        </Actions>
        {linkNote ? <Small role="status">{linkNote}</Small> : null}

        <Panel>
          <PanelHead>Mark as posted</PanelHead>
          <Hint>Use this only after the post is live. It is recorded as marked posted by an admin, with your name. Times are in {tz}.</Hint>
          <FieldLabel>
            Published date
            <TextInput type="date" value={pubDate} onChange={(e) => { setPubDate(e.target.value); report.edited(); }} />
          </FieldLabel>
          <FieldLabel>
            Published time
            <TextInput type="time" value={pubTime} onChange={(e) => { setPubTime(e.target.value); report.edited(); }} />
          </FieldLabel>
          <FieldLabel>
            Post URL (optional)
            <TextInput type="url" placeholder="https://" value={pubUrl} onChange={(e) => { setPubUrl(e.target.value); report.edited(); }} />
          </FieldLabel>
          <Actions>
            <Button size="lg" onClick={onReport} isLoading={report.busy}>
              Mark as posted
            </Button>
          </Actions>
          {timeErr ? <Banner $tone="danger">{timeErr}</Banner> : null}
          <ResultNotice result={report.result} okText="Recorded as marked posted by an admin. It is not verified by the platform." />
        </Panel>

        <Panel>
          <PanelHead>Owner link</PanelHead>
          <Hint>A new link replaces the old one. Nothing is re-sent and no new delivery is created.</Hint>
          <Actions>
            <Button variant="secondary" size="lg" onClick={onReissue} isLoading={reissue.busy}>
              Reissue owner link
            </Button>
          </Actions>
          <ResultNotice result={reissue.result} okText="New link created. The old link no longer works. Nothing was sent." />
          {link ? (
            <Banner $tone="info">
              <strong>This link is shown once.</strong>
              <Mono data-testid="owner-link">{link}</Mono>
              <Actions>
                <Button variant="secondary" size="lg" onClick={() => copyText(link).then((ok) => setCopied(ok ? "Link copied." : "Copy failed."))}>
                  Copy link
                </Button>
              </Actions>
            </Banner>
          ) : null}
        </Panel>
      </Stack>
    </Panel>
  );
};

export default OwnerPackPanel;
